/**
 * CrewSection — a Booking-assignment surface: it selects existing Company
 * Contractors, then saves/removes their Assignments. Roster creation and
 * editing live in Settings. The staged-crew model — state, transitions, pay,
 * frozen-ness, validity — lives in crew-payload.ts; this component is
 * rendering plus the picker.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useDismissable } from "../../lib/use-dismissable";
import { useListboxNav } from "../../lib/use-listbox-nav";
import { Tooltip } from "../ui/Tooltip";
import { ConfirmModal } from "../ui/ConfirmModal";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableFoot,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../ui/ResponsiveTable";
import { XIcon } from "../ui/icons";
import { AllocationConflictNotice } from "./AllocationConflictNotice";
import type { ContractorConflict } from "../../lib/allocation-api";
import { cx } from "@shared/cx";
import forms from "../ui/field.module.css";
import srOnly from "@shared/a11y.module.css";
import { scrollToFirstBlockingNoticeAfterPaint } from "../../lib/scroll-to-blocking-notice";
import {
  ASSIGNMENT_STATUS_COLORS,
  ASSIGNMENT_STATUS_LABELS,
} from "../../lib/status-config";
import { formatNZDCompact } from "@shared/nz-money";
import { isActive } from "../../lib/assignment-status";
import {
  assignedCrewHours,
  buildCrewSelection,
  crewRowInvalid,
  crewRowPay,
  deriveCrewRows,
  initStagedCrew,
  recordCancelledAssignment,
  stageContractor,
  unstageContractor,
  updateStagedRow,
  type CrewRow,
  type StagedCrew,
  type StagedCrewRow,
} from "../../lib/crew-payload";
import {
  useRegisterSavable,
  type SavableSections,
} from "../../lib/use-savable-sections";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";
import {
  removeAssignment,
  selectContractors,
  type WorkspaceAssignment,
} from "../../lib/event-workspace-api";
import {
  listJobSheetTemplates,
  type JobSheetTemplate,
} from "../../lib/job-sheet-templates-api";
import type { Contractor } from "../../lib/contractors-api";
import {
  listBookingVehicles,
  type BookingVehicle,
} from "../../lib/transport-api";
import { InlineBanner } from "../ui/InlineBanner";
import cell from "@/components/ui/cell-input.module.css";
import badge from "../../lib/status-badge.module.css";
import styles from "./CrewSection.module.css";

/** The workspace assignment slice this section reads — the staged-crew
 *  model's ExistingCrewRow plus the display-only fields. */
type Assignment = Pick<
  WorkspaceAssignment,
  | "id"
  | "contractorId"
  | "contractor"
  | "status"
  | "hourlyRate"
  | "estimatedHours"
  | "tasksDescription"
  | "vehicleName"
  | "jobsheetTemplateId"
  | "jobsheetTemplateName"
  | "payAmount"
>;

// Pre-fleet bookings stored the driver's vehicle as one of these sentinels on
// bookings.vehicle_type; migration 120 carried them onto the Assignment
// unchanged, so they still have to render legibly and stay selectable.
const LEGACY_VEHICLE_LABELS: Record<string, string> = {
  personal: "Contractor's Personal Vehicle",
  company_van: "Company Van",
  hire: "Hire Vehicle",
  admin_vehicle: "Admin's Personal Vehicle",
};

// The borderless-until-hover cell is `cell-input.module.css` — one
// treatment shared with the gear editor, the day schedule and the event
// card, rather than a seventh copy of the same declarations.

function buildRemoveDialogMessage(
  target: { name: string; status: string } | null,
): ReactNode {
  if (!target) return null;
  const name = <strong>{target.name}</strong>;
  switch (target.status) {
    case "notified":
    case "accepted":
      return (
        <>
          Remove {name} from this job? They will be emailed to let them know the
          offer has been cancelled.
        </>
      );
    case "pending":
      return (
        <>
          Remove {name} from this job? They haven&apos;t been notified yet, so
          no email will be sent.
        </>
      );
    case "declined":
    case "cancelled":
      return (
        <>
          Remove {name}&apos;s {target.status} record from this job? No email
          will be sent.
        </>
      );
    default:
      console.warn(
        "[CrewSection] Unknown assignment status in remove dialog:",
        target.status,
      );
      return <>Remove {name} from this job? No email will be sent.</>;
  }
}

interface CrewSectionProps {
  bookingId: string;
  token: string;
  assignments: Assignment[];
  allContractors: Contractor[];
  /**
   * Total labour hours on the quote, carried forward so crew hours are never
   * re-keyed (#181). Server-folded by internal/labour and shipped on the
   * workspace read (quotedLabour.total_hours, #242 C4). Null when unknowable
   * (no labour lines, or multiple packages before approval).
   */
  quotedLabourHours?: number | null;
  /**
   * Contractors this Booking shares with another Booking in a reserving
   * Phase on overlapping dates (#305). Read once by the workspace so this
   * section and the transport rows can never disagree. It warns — a crew row
   * carrying a clash still saves.
   */
  contractorConflicts?: ContractorConflict[];
  /** Workspace savable-sections registry — this section's pending edits are flushed via saveAll() before generation. */
  savables?: SavableSections;
  onCrewCountChange?: (count: number) => void;
  /** The workspace mutation ritual: busy state + error sink + refetch. */
  mutations: MutationRunner;
}

export function CrewSection({
  bookingId,
  token,
  assignments: existingAssignments,
  allContractors: contractors,
  quotedLabourHours = null,
  contractorConflicts = [],
  savables,
  onCrewCountChange,
  mutations,
}: CrewSectionProps) {
  const [crew, setCrew] = useState<StagedCrew>(() =>
    initStagedCrew(existingAssignments),
  );
  // Job sheet template picker options (#188). A load failure just leaves the
  // per-row select with only the Default option — not worth blocking crew.
  const [templates, setTemplates] = useState<JobSheetTemplate[]>([]);

  // The Booking's planned vehicles — the options a crew row can be put in.
  // Read here rather than passed down: this is the only surface that assigns
  // one, and it re-reads on mount so a Transport change needs no second save.
  const [bookingVehicles, setBookingVehicles] = useState<BookingVehicle[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    listJobSheetTemplates(ac.signal)
      .then(setTemplates)
      .catch(() => {});
    listBookingVehicles(bookingId, ac.signal)
      .then((res) => setBookingVehicles(res.vehicles))
      .catch(() => {});
    return () => ac.abort();
  }, [bookingId]);
  // The Booking's vehicles, plus any value already stored on a row that is no
  // longer among them — a legacy sentinel, or a vehicle since deselected in
  // Quoting → Transport. Dropping it would silently reassign that Contractor.
  const vehicleOptions = useMemo(() => {
    const names = bookingVehicles.map((v) => v.vehicle_name);
    for (const a of existingAssignments) {
      if (a.vehicleName && !names.includes(a.vehicleName)) {
        names.push(a.vehicleName);
      }
    }
    return names;
  }, [bookingVehicles, existingAssignments]);

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [assignmentToCancel, setAssignmentToCancel] = useState<{
    id: string;
    name: string;
    status: string;
  } | null>(null);

  useDismissable({
    open: dropdownOpen,
    onDismiss: () => setDropdownOpen(false),
    pointer: [dropdownRef],
  });

  const updateAssignment = (
    id: string,
    field: keyof StagedCrewRow,
    value: string,
  ) => setCrew((prev) => updateStagedRow(prev, id, field, value));

  // Plain closure over current state — useRegisterSavable always invokes the
  // latest render's save, so no useCallback/state-mirroring refs are needed.
  // The staged/preserved/cancelled merge rules live in crew-payload.ts.
  //
  // Crew does NOT publish a section-dirty flag: crew edits are staged, then
  // saved as one full replace + refetch, so job-summary staleness picks the
  // change up from the projection compare (stored snapshot vs the refetched
  // read, which orders assignments by content, not by the freshly minted ids).
  const saveCrew = async () => {
    const selection = buildCrewSelection(crew, existingAssignments);
    if (selection.kind === "nothing-to-save") return;
    if (selection.kind === "invalid") {
      setShowValidation(true);
      scrollToFirstBlockingNoticeAfterPaint();
      return;
    }
    setShowValidation(false);
    await selectContractors(token, bookingId, selection.assignments);
  };

  useRegisterSavable(savables, "crew", saveCrew);

  // The row source, the commercial rules, and the counts are all derived from
  // the one staged-crew model — nothing here re-implements them.
  const crewRows = deriveCrewRows(crew, existingAssignments);

  const totalCrewCount = crewRows.length;
  useEffect(() => {
    onCrewCountChange?.(totalCrewCount);
  }, [totalCrewCount, onCrewCountChange]);

  const isActivelyAssigned = (contractorId: string) => {
    if (crew.staged[contractorId]) return true;
    return existingAssignments.some(
      (a) => a.contractorId === contractorId && isActive(a),
    );
  };

  const filteredContractors = contractors.filter((c) => {
    if (isActivelyAssigned(c.id)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.skills?.some((s) => s.toLowerCase().includes(q))
    );
  });

  const pickContractor = (c: Contractor) => {
    setCrew((prev) =>
      stageContractor(prev, existingAssignments, c.id, quotedLabourHours),
    );
    setSearchQuery("");
    setDropdownOpen(false);
  };

  const pickerNav = useListboxNav({
    open: dropdownOpen && filteredContractors.length > 0,
    count: filteredContractors.length,
    onCommit: (i) => pickContractor(filteredContractors[i]),
  });

  const tableTotalPay = crewRows.reduce(
    (sum, r) => sum + (crewRowPay(r) ?? 0),
    0,
  );

  // Quoted-vs-assigned reconciliation (#181).
  const assignedHours = assignedCrewHours(crewRows);

  const conflictsByContractor = useMemo(() => {
    const map = new Map<string, ContractorConflict["clashes"]>();
    for (const c of contractorConflicts) map.set(c.contractor_id, c.clashes);
    return map;
  }, [contractorConflicts]);

  // Prefer the Company roster copy when present, with the Assignment snapshot
  // as a history-safe fallback.
  const contractorForRow = (row: CrewRow<Assignment>): Contractor | null =>
    contractors.find((x) => x.id === row.contractorId) ??
    row.assignment?.contractor ??
    null;

  // The blank option's label: the template the row inherits without an
  // override — the server-resolved effective name on a sent row with no
  // override, else the Contractor's default, else the Company default. With
  // no default anywhere the row falls back to the full-detail floor (#188).
  const inheritedTemplateName = (
    row: CrewRow<Assignment>,
    contractor: Contractor | null,
  ): string => {
    if (!row.editable && row.assignment && !row.assignment.jobsheetTemplateId) {
      return row.assignment.jobsheetTemplateName ?? "Full detail";
    }
    const contractorDefault = contractor?.jobsheet_template_id
      ? templates.find((t) => t.id === contractor.jobsheet_template_id)
      : undefined;
    return (
      (contractorDefault ?? templates.find((t) => t.is_default))?.name ??
      "Full detail"
    );
  };

  const removeRow = (row: CrewRow<Assignment>) => {
    // A sent row is cancelled server-side behind a confirm; a staged row is
    // local state and just goes.
    if (!row.editable && row.assignment) {
      setAssignmentToCancel({
        id: row.assignment.id,
        name: contractorForRow(row)?.name || "Unknown",
        status: row.assignment.status,
      });
      return;
    }
    setCrew((prev) => unstageContractor(prev, row.contractorId));
  };

  return (
    <>
      {/* Crew only selects existing Company Contractors. Roster management is
          intentionally confined to Settings. */}
      <div className={styles.picker} ref={dropdownRef}>
        <div className={styles.pickerField}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            onKeyDown={pickerNav.onKeyDown}
            {...pickerNav.comboboxProps}
            placeholder="Search contractors..."
            aria-label="Add contractor to crew"
            className={forms.input}
          />
          {dropdownOpen && (
            <div
              id={pickerNav.listboxId}
              role="listbox"
              aria-label="Contractor suggestions"
              className={styles.menu}
            >
              {filteredContractors.length === 0 ? (
                <p className={styles.empty}>
                  {searchQuery
                    ? "No matching contractors"
                    : "All contractors assigned"}
                </p>
              ) : (
                filteredContractors.map((c, i) => (
                  <button
                    key={c.id}
                    id={pickerNav.optionId(i)}
                    type="button"
                    role="option"
                    aria-selected={i === pickerNav.activeIndex}
                    tabIndex={-1}
                    onClick={() => pickContractor(c)}
                    className={cx(
                      styles.option,
                      i === pickerNav.activeIndex && styles.optionActive,
                    )}
                  >
                    <div>
                      <p className={styles.optionName}>{c.name}</p>
                      <p className={styles.optionEmail}>{c.email}</p>
                    </div>
                    {c.skills?.length > 0 && (
                      <div className={styles.skills}>
                        {c.skills.slice(0, 2).map((s) => (
                          <span
                            key={s}
                            className={styles.skill}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {crewRows.length === 0 ? (
        <p className={styles.noCrew}>
          No crew assigned. Select a Contractor above.
        </p>
      ) : (
        // Cards below md, a plain table at and above it (the section sits in
        // the workspace column, so the nine-column grid needs md). No overflow
        // wrapper: the cards pattern adds no overflow ancestor, and the
        // contractor picker above plus the per-row selects stay unclipped.
        <ResponsiveTable breakpoint="md">
          <TableHead>
            <TableHeaderCell padding="header">Contractor</TableHeaderCell>
            <TableHeaderCell padding="header" width="24">
              <Tooltip content="Multiplied by hours to give this Contractor's estimated pay.">
                Rate $/hr
              </Tooltip>
            </TableHeaderCell>
            <TableHeaderCell padding="header" width="24">
              <Tooltip content="Multiplied by the rate to give this Contractor's estimated pay.">
                Hours
              </Tooltip>
            </TableHeaderCell>
            <TableHeaderCell padding="header" align="right" width="24">
              Pay
            </TableHeaderCell>
            <TableHeaderCell padding="header">
              <Tooltip content="What this Contractor is doing, and anything meant for them alone — one line each, printed on their job sheet.">
                Task &amp; notes
              </Tooltip>
            </TableHeaderCell>
            <TableHeaderCell padding="header" width="36">
              Job sheet
            </TableHeaderCell>
            <TableHeaderCell padding="header" width="24">
              Status
            </TableHeaderCell>
            <TableHeaderCell padding="header" width="8">
              <span className={srOnly.srOnly}>Remove</span>
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {crewRows.map((row) => {
              const contractor = contractorForRow(row);
              const name = contractor?.name || "Unknown";
              const invalid = showValidation
                ? crewRowInvalid(row)
                : { rate: false, hours: false };
              const pay = crewRowPay(row);
              const status = row.assignment?.status ?? null;
              return (
                // columns={1}: many narrow inputs stack label-above-value on
                // the mobile card, like the day-schedule table.
                <TableRow key={row.key} columns={1}>
                  {/* cornerGap reserves room for the corner remove button. */}
                  <TableCell padding="tight" cornerGap="md">
                    <div className={styles.nameCell}>
                      <p className={styles.name}>{name}</p>
                      <p className={styles.email}>
                        {contractor?.email}
                      </p>
                      {/* Row-level, beside the person it concerns (#305).
                            Never a section Banner — see the notice component. */}
                      <AllocationConflictNotice
                        lead="Also booked on"
                        clashes={
                          conflictsByContractor.get(row.contractorId) ?? []
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell
                    label="Rate $/hr"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {row.editable ? (
                      <>
                        <input
                          type="number"
                          aria-label={`${name} Rate $/hr`}
                          value={row.staged?.hourly_rate ?? ""}
                          onChange={(e) =>
                            updateAssignment(
                              row.contractorId,
                              "hourly_rate",
                              e.target.value,
                            )
                          }
                          aria-invalid={invalid.rate || undefined}
                          className={cx(cell.cell, invalid.rate && cell.invalid)}
                        />
                        {invalid.rate && (
                          <InlineBanner variant="error">
                            Enter an hourly rate
                          </InlineBanner>
                        )}
                      </>
                    ) : (
                      <span className={styles.readonly}>
                        {row.assignment?.hourlyRate ?? "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    label="Hours"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {row.editable ? (
                      <>
                        <input
                          type="number"
                          step="0.5"
                          aria-label={`${name} Hours`}
                          value={row.staged?.estimated_hours ?? ""}
                          onChange={(e) =>
                            updateAssignment(
                              row.contractorId,
                              "estimated_hours",
                              e.target.value,
                            )
                          }
                          aria-invalid={invalid.hours || undefined}
                          className={cx(cell.cell, invalid.hours && cell.invalid)}
                        />
                        {invalid.hours && (
                          <InlineBanner variant="error">
                            Enter estimated hours
                          </InlineBanner>
                        )}
                      </>
                    ) : (
                      <span className={styles.readonly}>
                        {row.assignment?.estimatedHours ?? "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    label="Pay"
                    align="right"
                    padding="tight"
                    className={cx(styles.alignTop, styles.nowrapMd)}
                  >
                    <span className={styles.pay}>
                      {pay != null ? formatNZDCompact(pay) : "—"}
                    </span>
                  </TableCell>
                  <TableCell
                    label="Task & notes"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {row.staged ? (
                      <textarea
                        aria-label={`${name} Task & notes`}
                        value={row.staged.tasks_description}
                        onChange={(e) =>
                          updateAssignment(
                            row.contractorId,
                            "tasks_description",
                            e.target.value,
                          )
                        }
                        rows={1}
                        className={cx(cell.cell, styles.resizable)}
                      />
                    ) : (
                      <span className={styles.readonly}>
                        {row.assignment?.tasksDescription || ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    label="Vehicle"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {row.staged ? (
                      <select
                        aria-label={`${name} Vehicle`}
                        value={row.staged.vehicle_name}
                        onChange={(e) =>
                          updateAssignment(
                            row.contractorId,
                            "vehicle_name",
                            e.target.value,
                          )
                        }
                        className={cell.cell}
                      >
                        <option value="">None</option>
                        {vehicleOptions.map((v) => (
                          <option key={v} value={v}>
                            {LEGACY_VEHICLE_LABELS[v] ?? v}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={styles.readonly}>
                        {row.assignment?.vehicleName
                          ? (LEGACY_VEHICLE_LABELS[
                              row.assignment.vehicleName
                            ] ?? row.assignment.vehicleName)
                          : "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    label="Job sheet"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {row.staged ? (
                      <select
                        aria-label={`${name} Job sheet`}
                        value={row.staged.jobsheet_template_id}
                        onChange={(e) =>
                          updateAssignment(
                            row.contractorId,
                            "jobsheet_template_id",
                            e.target.value,
                          )
                        }
                        className={cell.cell}
                      >
                        <option value="">
                          Default ({inheritedTemplateName(row, contractor)})
                        </option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={styles.readonly}>
                        {row.assignment?.jobsheetTemplateName ?? "Default"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    label="Status"
                    padding="tight"
                    className={styles.alignTop}
                  >
                    {status ? (
                      <span
                        className={cx(
                          badge.badge,
                          ASSIGNMENT_STATUS_COLORS[status] ?? badge.gray,
                        )}
                      >
                        {ASSIGNMENT_STATUS_LABELS[status] || status}
                      </span>
                    ) : (
                      <span className={styles.notSent}>Not sent</span>
                    )}
                  </TableCell>
                  <TableCell corner padding="tight" className={styles.alignTop}>
                    <button
                      onClick={() => removeRow(row)}
                      className={styles.remove}
                      aria-label={`Remove ${name}`}
                    >
                      <XIcon className={styles.removeIcon} />
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFoot>
            <TableRow variant="plain" className={styles.footRow}>
              <TableCell
                padding="header"
                className={styles.footCount}
                colSpan={3}
              >
                {crewRows.length} contractor{crewRows.length !== 1 ? "s" : ""}
              </TableCell>
              <TableCell
                label="Total pay"
                align="right"
                padding="header"
                className={cx(styles.footTotal, styles.nowrapMd)}
              >
                {formatNZDCompact(tableTotalPay)}
              </TableCell>
              <TableCell colSpan={5} padding="none" />
            </TableRow>
          </TableFoot>
        </ResponsiveTable>
      )}
      {quotedLabourHours != null && (
        <p
          className={cx(
            styles.reconcile,
            assignedHours !== quotedLabourHours && styles.reconcileMismatch,
          )}
        >
          Quoted {quotedLabourHours} hrs · Assigned {assignedHours} hrs
        </p>
      )}
      <ConfirmModal
        open={!!assignmentToCancel}
        title="Remove Contractor"
        message={buildRemoveDialogMessage(assignmentToCancel)}
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={
          !!assignmentToCancel &&
          mutations.isPending(
            MUTATION_KEYS.removeAssignment(assignmentToCancel.id),
          )
        }
        onConfirm={async () => {
          if (!assignmentToCancel) return;
          const target = assignmentToCancel;
          // The workspace mutation ritual: busy via isPending, failure into
          // the runner's error sink, refetch on success — like every sibling.
          await mutations.run(
            MUTATION_KEYS.removeAssignment(target.id),
            `Remove ${target.name}`,
            () => removeAssignment(bookingId, target.id),
            {
              onSuccess: () =>
                setCrew((prev) =>
                  recordCancelledAssignment(
                    prev,
                    existingAssignments,
                    target.id,
                  ),
                ),
            },
          );
          setAssignmentToCancel(null);
        }}
        onCancel={() => setAssignmentToCancel(null)}
      />
    </>
  );
}
